import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { logger } from "@/infrastructure/logger";
import {
  authRepository,
  SessionData,
  AuthToken,
} from "@/infrastructure/authentication";
import { authService } from "@/domain/services/authService";
import { AuthContext } from "@/application/contexts/AuthContext";
import { ROUTES } from "@/domain/constants/routes";
import { contextService } from "@/domain/services/contextService";

// 創建認證服務專用的日誌器
const log = logger.createLogger("authService");

/**
 * 認證服務 Hook
 * 提供認證相關的基礎功能，封裝對基礎設施層的訪問
 */
export function useAuthService() {
  return {
    /**
     * 檢查是否有有效的會話
     */
    hasValidSession: () => {
      try {
        const hasSession = authRepository.hasValidSession();
        return hasSession;
      } catch (error) {
        console.error("Failed to get current user:", error);
        return false;
      }
    },
    getCurrentUser: () => {
      return authService.getCurrentUser();
    },

    /**
     * 結束用戶會話（登出）
     */
    logout: () => {
      log.info("用戶登出，結束會話");
      authRepository.terminateSession();
    },

    /**
     * 獲取認證 token
     */
    getToken: (): AuthToken | null => {
      return authRepository.retrieveToken();
    },

    /**
     * 獲取用戶會話資訊
     */
    getSession: () => {
      const session = authRepository.retrieveSession();
      return session;
    },

    /**
     * 創建用戶會話
     */
    createSession: (sessionData: SessionData) => {
      log.info("創建新的用戶會話");
      authRepository.createSession(sessionData);
    },

    /**
     * 儲存 Google 登入憑證
     * @param credential Google 登入憑證
     */
    saveGoogleCredential: (credential: string) => {
      log.info("儲存 Google 登入憑證");
      // 這裡可以將 Google 憑證儲存到本地存儲或進行其他處理
      // 實際實現可能需要調用 API 來驗證和處理 Google 憑證
      // authRepository.saveGoogleCredential(credential);
      return credential;
    },
  };
}

/**
 * useAuth 鉤子
 * 提供對認證上下文的訪問，包括認證狀態和方法
 * 使用 useAuthService 來處理基礎認證功能
 *
 * @returns 認證上下文和擴展方法
 */
export function useAuth() {
  const context = useContext(AuthContext);
  const authService = useAuthService();

  if (context === undefined) {
    throw new Error("useAuth 必須在 AuthProvider 內部使用");
  }

  // 擴展 AuthContext 提供的功能，整合 useAuthService 的功能
  return {
    ...context,

    // 快速檢查會話有效性，不需要訪問上下文
    checkSession: () => {
      log.debug("快速檢查會話有效性");
      return authService.hasValidSession();
    },

    // 取得當前 token，不需要訪問上下文
    getAuthToken: () => {
      log.debug("取得當前認證 token");
      return authService.getToken();
    },
  };
}

export function useLogin() {
  const { oauthWithGoogle, loginWithGoogle, loading } = useAuth();
  // const { context, refreshContext } = useUserContext();
  const authService = useAuthService();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  /**
   * 處理 Google 登入成功
   * @param credential Google 登入憑證，可以是字串或包含 id_token 和 access_token 的物件
   */
  const handleLoginSuccess = async (credential: string) => {
    try {
      // 使用 Google 憑證登入
      await loginWithGoogle(credential);

      const userContext = await contextService.getContext();
      if (userContext.is_new_user) {
        navigate(ROUTES.CONVERSATION, { replace: true });
      } else {
        navigate(ROUTES.AGENTS, { replace: true });
      }
    } catch (error) {
      log.error("登入失敗:", error);
      setError("登入失敗，請稍後再試");
    }
  };

  const handleGoogleOauth = async (oauthResponse: object) => {
    try {
      // 使用 Google 憑證登入
      await oauthWithGoogle(oauthResponse);

      const userContext = await contextService.getContext();
      if (userContext.is_new_user) {
        navigate(ROUTES.CONVERSATION, { replace: true });
      } else {
        navigate(ROUTES.AGENTS, { replace: true });
      }
    } catch (error) {
      log.error("登入失敗:", error);
      setError("登入失敗，請稍後再試");
    }
  };

  /**
   * 清除錯誤訊息
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * 檢查用戶是否已登入
   */
  const checkAuthenticated = () => {
    return authService.hasValidSession();
  };

  return {
    loading,
    error,
    handleLoginSuccess,
    handleGoogleOauth,
    clearError,
    checkAuthenticated,
  };
}
